// Modal Management - Base Utilities

// Track the element that had focus before modal opened
let previouslyFocusedElement = null;

// Focusable element selectors
const FOCUSABLE_SELECTORS = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])'
].join(', ');

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} container - The container to search
 * @returns {HTMLElement[]} Array of focusable elements
 */
function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS))
        .filter(el => el.offsetParent !== null); // Filter hidden elements
}

/**
 * Handle keydown for focus trapping
 * @param {KeyboardEvent} event - The keydown event
 * @param {HTMLElement} modal - The modal element
 */
function handleFocusTrap(event, modal) {
    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements(modal);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
        // Shift+Tab: if on first element, go to last
        if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        }
    } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    }
}

/**
 * Open a modal by ID
 * @param {string} id - Modal element ID
 */
export function openModal(id) {
    const modal = document.getElementById(id);
    if (modal) {
        // Store currently focused element
        previouslyFocusedElement = document.activeElement;

        modal.classList.add('active');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';

        // Set up focus trap
        const focusTrapHandler = (e) => handleFocusTrap(e, modal);
        modal._focusTrapHandler = focusTrapHandler;
        modal.addEventListener('keydown', focusTrapHandler);

        // Move focus to first focusable element or modal content
        requestAnimationFrame(() => {
            const focusableElements = getFocusableElements(modal);
            if (focusableElements.length > 0) {
                // Try to focus a close button or primary action first
                const closeBtn = modal.querySelector('.modal-close');
                const firstInput = modal.querySelector('input:not([type="hidden"]), select, textarea');

                if (firstInput) {
                    firstInput.focus();
                } else if (closeBtn) {
                    closeBtn.focus();
                } else {
                    focusableElements[0].focus();
                }
            } else {
                // No focusable elements, focus the modal content itself
                const content = modal.querySelector('.modal-content');
                if (content) {
                    content.setAttribute('tabindex', '-1');
                    content.focus();
                }
            }
        });

        // Set aria attributes
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('role', 'dialog');
    }
}

/**
 * Close a modal by ID
 * @param {string} id - Modal element ID
 */
export function closeModal(id) {
    const modal = document.getElementById(id);
    if (modal) {
        modal.classList.remove('active');
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';

        // Remove focus trap handler
        if (modal._focusTrapHandler) {
            modal.removeEventListener('keydown', modal._focusTrapHandler);
            delete modal._focusTrapHandler;
        }

        // Restore focus to previously focused element
        if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
            previouslyFocusedElement.focus();
            previouslyFocusedElement = null;
        }
    }
}

/**
 * Setup modal backdrop click handlers
 * Closes modal when clicking outside the modal content
 * @param {Function} [onClose] - Optional callback when modal is closed via backdrop
 */
export function initModalBackdropHandlers(onClose) {
    document.querySelectorAll('.modal').forEach(modal => {
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                modal.classList.remove('active');
                document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
                if (onClose) {
                    onClose(modal);
                }
            }
        });
    });
}

/**
 * Close modal with common cleanup
 * @param {string} modalId - Modal element ID
 * @param {Object} options - Cleanup options
 * @param {string} [options.formId] - Form to reset
 * @param {string[]} [options.errorFields] - Fields to clear errors from
 * @param {Function} [options.clearErrorFn] - Function to clear field errors
 * @param {Function} [options.resetWidgetFn] - Function to reset any widget (e.g., Turnstile)
 * @param {*} [options.widgetId] - Widget ID to pass to resetWidgetFn
 * @param {HTMLElement} [options.restoreFocus] - Element to restore focus to
 */
export function closeModalWithCleanup(modalId, options = {}) {
    const {
        formId,
        errorFields = [],
        clearErrorFn,
        resetWidgetFn,
        widgetId,
        restoreFocus
    } = options;

    const modal = document.getElementById(modalId);
    if (!modal) return;

    modal.classList.remove('active');
    document.body.style.overflow = '';

    if (formId) {
        const form = document.getElementById(formId);
        if (form) form.reset();
    }

    if (clearErrorFn && errorFields.length > 0) {
        errorFields.forEach(field => {
            clearErrorFn(field, field + 'Error');
        });
    }

    if (resetWidgetFn && widgetId !== undefined) {
        resetWidgetFn(widgetId);
    }

    if (restoreFocus && typeof restoreFocus.focus === 'function') {
        restoreFocus.focus();
    }
}
